import React from 'react';
import {ArrowTopRightOnSquareIcon, ChevronRightIcon} from '@heroicons/react/24/outline';
import {Link, useNavigate} from 'react-router';

import ProItemCard from './Components/ProItemCard';
import {RESOURCE_GRID_CLASSES, ResourceNotice, ResourceQueryState} from './LoadingCard';

/*
 * ProCategories is the index grid both resource families share. The pieces
 * below it are the route chrome the six resource routes share: the page title,
 * the breadcrumb trail and the external resource link.
 */

export const RESOURCE_TITLE_CLASSES =
  'm-0 min-w-0 text-2xl font-bold break-words text-(--compass-on-surface) sm:text-3xl';

/** A link to an external resource, which opens in a new browsing context. */
export const ResourceExternalLink = ({href, children}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex min-h-11 min-w-11 items-center gap-1 rounded-md text-(--compass-primary) underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)">
    <span className="min-w-0 break-words">
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </span>
    <ArrowTopRightOnSquareIcon className="size-4 shrink-0" aria-hidden="true" />
  </a>
);

/**
 * The trail every resource route opens with. Each item with `to` is a link;
 * the last item without one is the current page.
 */
export const ResourceBreadcrumbs = ({items}) => (
  <nav aria-label="Breadcrumb" className="mb-4">
    <ol className="m-0 flex list-none flex-wrap items-center gap-x-1 p-0 text-sm">
      {items.map((item, index) => (
        <li key={`${index}-${item.label}`} className="flex min-w-0 items-center gap-x-1">
          {index > 0 ? <ChevronRightIcon className="size-4 shrink-0" aria-hidden="true" /> : null}
          {item.to ? (
            <Link
              to={item.to}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-1 break-words text-(--compass-on-surface) underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)">
              {item.label}
            </Link>
          ) : (
            <span aria-current="page" className="px-1 font-semibold break-words">
              {item.label}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

const ProCategories = ({categories, error, refetch, resourceLabel}) => {
  const navigate = useNavigate();

  return (
    <ResourceQueryState
      data={categories}
      error={error}
      refetch={refetch}
      label={resourceLabel}
      placeholderCount={7}>
      {categories?.length ? (
        <ul data-testid="pro-categories" className={RESOURCE_GRID_CLASSES}>
          {categories.map((category) => (
            <li key={category.id} className="min-w-0">
              <ProItemCard
                item={category}
                description={category.description}
                titleAs="h2"
                onClick={() => navigate(encodeURIComponent(category.slug))}
              />
            </li>
          ))}
        </ul>
      ) : (
        <ResourceNotice>{`No ${resourceLabel} are available yet.`}</ResourceNotice>
      )}
    </ResourceQueryState>
  );
};

export default ProCategories;
